import { useEffect, useState } from "react";
import { Gift, Zap, Loader2, Check, Star, DollarSign, Trophy, Crown, Award, Ticket, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import apiClient, { getErrorMessage } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, FullPageLoader, EmptyState } from "@/components/common";

const promoIcon = {
  free_session: { icon: Star, color: "#00ff9d", label: "Free Session" },
  discount: { icon: DollarSign, color: "#ffb020", label: "Credit" },
  tournament_entry: { icon: Trophy, color: "#00e5ff", label: "Tournament" },
  vip_package: { icon: Crown, color: "#8b5cf6", label: "VIP" },
  gift_card: { icon: Gift, color: "#ff2e97", label: "Gift Card" },
  merchandise: { icon: Gift, color: "#ff2e97", label: "Merch" },
};

const MEMBERSHIP_TIERS = [
  { key: "basic", label: "Basic", color: "#94a3b8", costKey: "membership_basic_cost" },
  { key: "pro", label: "Pro", color: "#00e5ff", costKey: "membership_pro_cost" },
  { key: "champ", label: "Champ", color: "#00ff9d", costKey: "membership_champ_cost" },
  { key: "vip", label: "VIP", color: "#ff2e97", costKey: "membership_vip_cost" },
];

function Stepper({ value, setValue, min = 1, max = 99, testId }) {
  return (
    <div className="inline-flex items-center gap-2">
      <button className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "rgba(148,163,184,0.15)" }} onClick={() => setValue(Math.max(min, value - 1))} data-testid={`${testId}-minus`}><Minus className="w-4 h-4" /></button>
      <div className="w-10 text-center font-display font-bold text-white" data-testid={testId}>{value}</div>
      <button className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "rgba(148,163,184,0.15)" }} onClick={() => setValue(Math.min(max, value + 1))} data-testid={`${testId}-plus`}><Plus className="w-4 h-4" /></button>
    </div>
  );
}

export default function Rewards() {
  const { user, refreshUser } = useAuth();
  const [rewards, setRewards] = useState([]);
  const [claimRules, setClaimRules] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const [creditQty, setCreditQty] = useState(1);
  const [entryQty, setEntryQty] = useState(1);
  const [giftQty, setGiftQty] = useState(1);

  useEffect(() => {
    Promise.all([apiClient.get("/rewards"), apiClient.get("/claim-rules")])
      .then(([rw, cr]) => { setRewards(rw.data); setClaimRules(cr.data); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  const points = user?.points ?? 0;

  const doRedeem = async (kind, url, body, successMsg) => {
    if (busy) return;
    setBusy(kind);
    try {
      const { data } = await apiClient.post(url, body || {});
      await refreshUser();
      toast.success(successMsg || data.message || "Redeemed!", {
        description: data.redemption_code ? `Code: ${data.redemption_code}` : undefined,
      });
    } catch (err) { toast.error(getErrorMessage(err, "Redeem failed")); }
    finally { setBusy(""); }
  };

  const claimPromo = async (reward) => {
    if (points < reward.points_required) { toast.error("Not enough points for this reward"); return; }
    await doRedeem(`promo-${reward.id}`, `/rewards/${reward.id}/claim`, null, "Reward claimed!");
  };

  if (loading) return <FullPageLoader label="Loading Rewards" />;

  const creditUnit = Number(claimRules?.booking_credit_cost || 0);
  const entryUnit = Number(claimRules?.tournament_entry_cost || 0);
  const giftUnit = Number(claimRules?.gift_card_5_cost || 0);

  return (
    <div>
      <PageHeader
        title="Power Rewards"
        subtitle="Redeem your points for real perks."
        right={
          <div className="chip" style={{ background: "rgba(255,176,32,0.12)", border: "1px solid rgba(255,176,32,0.4)", color: "#ffb020" }} data-testid="rewards-points">
            <Zap className="w-4 h-4" /> {points.toLocaleString()} Points
          </div>
        }
      />

      {/* Balances */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <BalanceCard label="Points" value={points.toLocaleString()} color="#ffb020" Icon={Zap} testId="bal-points" />
        <BalanceCard label="Booking credits (30 min)" value={user?.free_session_credits ?? 0} color="#00ff9d" Icon={Ticket} testId="bal-credits" />
        <BalanceCard label="Tournament entries" value={user?.free_tournament_entries ?? 0} color="#00e5ff" Icon={Trophy} testId="bal-entries" />
        <BalanceCard label="Account credit" value={`$${(user?.credit_balance ?? 0).toFixed(2)}`} color="#ff2e97" Icon={DollarSign} testId="bal-cash" />
      </div>

      {/* 30-min Booking credit */}
      <RedeemCard
        title="Booking credit — 30 minutes"
        subtitle="Each credit = 30 min on one station or a 30-min motion add-on."
        Icon={Ticket}
        color="#00ff9d"
        unitCost={creditUnit}
        unitLabel="30 min credit"
        qty={creditQty}
        setQty={setCreditQty}
        points={points}
        busy={busy === "credit"}
        onRedeem={() => doRedeem("credit", "/rewards/redeem/booking-credit", { qty: creditQty }, `${creditQty} × 30-min credit added`)}
        testIdPrefix="redeem-credit"
      />

      {/* Free tournament entry */}
      <RedeemCard
        title="Free tournament entry"
        subtitle="Waives the entry fee on any tournament you register for."
        Icon={Trophy}
        color="#00e5ff"
        unitCost={entryUnit}
        unitLabel="entry"
        qty={entryQty}
        setQty={setEntryQty}
        points={points}
        busy={busy === "entry"}
        onRedeem={() => doRedeem("entry", "/rewards/redeem/tournament-entry", { qty: entryQty }, `${entryQty} × free entry added`)}
        testIdPrefix="redeem-entry"
      />

      {/* Gift card */}
      <RedeemCard
        title="Gift card"
        subtitle="Redeems as cash-value account credit in $5 increments."
        Icon={Gift}
        color="#ff2e97"
        unitCost={giftUnit}
        unitLabel="$5"
        qty={giftQty}
        setQty={setGiftQty}
        points={points}
        busy={busy === "gift"}
        summary={`$${giftQty * 5}`}
        onRedeem={() => doRedeem("gift", "/rewards/redeem/gift-card", { qty: giftQty }, `$${giftQty * 5} added to your account credit`)}
        testIdPrefix="redeem-gift"
      />

      {/* Membership */}
      <div className="glass rounded-2xl p-6 mb-6" data-testid="redeem-membership-panel">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: "rgba(139,92,246,0.15)", border: "1px solid rgba(139,92,246,0.5)" }}>
            <Award className="w-5 h-5" style={{ color: "#8b5cf6" }} />
          </div>
          <div>
            <h3 className="font-display text-lg font-bold text-white">Membership · 30 days</h3>
            <p className="text-slate-400 text-sm">Redeem points to activate any membership tier for one month.</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {MEMBERSHIP_TIERS.map((t) => {
            const cost = Number(claimRules?.[t.costKey] || 0);
            const affordable = points >= cost && cost > 0;
            return (
              <div key={t.key} className="p-4 rounded-xl" style={{ background: "rgba(8,11,22,0.6)", border: `1px solid ${t.color}55` }}>
                <p className="font-display text-white font-bold" style={{ color: t.color }}>{t.label}</p>
                <p className="text-slate-500 text-xs mb-3">30-day membership</p>
                <p className="font-display text-lg font-bold mb-3" style={{ color: "#ffb020" }}><Zap className="w-4 h-4 inline mb-0.5" /> {cost.toLocaleString()}</p>
                <button
                  disabled={!affordable || busy === `mem-${t.key}`}
                  onClick={() => doRedeem(`mem-${t.key}`, "/rewards/redeem/membership", { membership_type: t.key }, `${t.label} activated for 30 days`)}
                  className={affordable ? "neon-btn w-full py-2" : "neon-outline-btn w-full py-2"}
                  data-testid={`redeem-membership-${t.key}`}
                >
                  {busy === `mem-${t.key}` ? <><Loader2 className="w-4 h-4 animate-spin" /> Redeeming...</>
                    : affordable ? "Redeem" : cost > 0 ? `Need ${(cost - points).toLocaleString()} more` : "Unavailable"}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Custom promos */}
      {rewards.length > 0 && (
        <>
          <PageHeader title="Special promos" subtitle="Limited-time rewards from the crew." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {rewards.map((r) => {
              const meta = promoIcon[r.reward_type] || promoIcon.gift_card;
              const affordable = points >= r.points_required;
              const outOfStock = r.stock_quantity != null && r.claimed_count >= r.stock_quantity;
              return (
                <div key={r.id} className="glass glass-hover rounded-2xl p-6 flex flex-col" data-testid={`reward-${r.id}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${meta.color}1f`, border: `1px solid ${meta.color}55` }}>
                      <meta.icon className="w-6 h-6" style={{ color: meta.color }} />
                    </div>
                    <span className="chip" style={{ background: `${meta.color}18`, border: `1px solid ${meta.color}44`, color: meta.color }}>{meta.label}</span>
                  </div>
                  <h3 className="font-display text-lg font-bold text-white mb-1">{r.title}</h3>
                  <p className="text-slate-400 text-sm flex-1 mb-4">{r.description}</p>
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-display text-xl font-bold" style={{ color: "#ffb020" }}>
                      <Zap className="w-4 h-4 inline mb-0.5" /> {r.points_required.toLocaleString()}
                    </span>
                    {r.stock_quantity != null && (
                      <span className="text-slate-500 text-xs">{Math.max(0, r.stock_quantity - r.claimed_count)} left</span>
                    )}
                  </div>
                  <button disabled={!affordable || outOfStock || busy === `promo-${r.id}`} onClick={() => claimPromo(r)}
                    className={affordable && !outOfStock ? "neon-btn w-full py-3" : "neon-outline-btn w-full py-3"}
                    data-testid={`claim-${r.id}`}>
                    {busy === `promo-${r.id}` ? <><Loader2 className="w-4 h-4 animate-spin" /> Claiming...</>
                      : outOfStock ? "Out of Stock"
                      : affordable ? <><Check className="w-4 h-4" /> Redeem</>
                      : `Need ${(r.points_required - points).toLocaleString()} more`}
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}

      {rewards.length === 0 && (
        <EmptyState icon={Gift} title="No custom promos right now" subtitle="Keep an eye out for limited-time perks." />
      )}
    </div>
  );
}

function BalanceCard({ label, value, color, Icon, testId }) {
  return (
    <div className="rounded-xl p-4" data-testid={testId} style={{ background: "rgba(8,11,22,0.6)", border: `1px solid ${color}44` }}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5" style={{ color }} />
        <p className="text-slate-400 text-xs uppercase tracking-wider">{label}</p>
      </div>
      <p className="font-display text-2xl font-bold text-white">{value}</p>
    </div>
  );
}

function RedeemCard({ title, subtitle, Icon, color, unitCost, unitLabel, qty, setQty, points, busy, onRedeem, testIdPrefix, summary }) {
  const totalCost = unitCost * qty;
  const affordable = points >= totalCost && unitCost > 0;
  return (
    <div className="glass rounded-2xl p-6 mb-6" data-testid={`${testIdPrefix}-panel`}>
      <div className="flex flex-wrap items-center gap-4">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: `${color}18`, border: `1px solid ${color}55` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-lg font-bold text-white">{title}</h3>
          <p className="text-slate-400 text-sm">{subtitle}</p>
        </div>
        <div className="text-right">
          <p className="text-slate-500 text-xs uppercase tracking-wider">Cost per {unitLabel}</p>
          <p className="font-display text-lg font-bold" style={{ color: "#ffb020" }}><Zap className="w-4 h-4 inline mb-0.5" /> {unitCost.toLocaleString()}</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-4 mt-4">
        <div>
          <p className="text-slate-500 text-xs uppercase tracking-wider mb-2">Quantity</p>
          <Stepper value={qty} setValue={setQty} testId={`${testIdPrefix}-qty`} />
        </div>
        <div className="ml-auto text-right">
          <p className="text-slate-500 text-xs uppercase tracking-wider">Total {summary ? "value" : ""}</p>
          <p className="font-display text-xl font-bold text-white">{summary || `${totalCost.toLocaleString()} pts`}</p>
          {summary && <p className="text-slate-400 text-xs">{totalCost.toLocaleString()} pts</p>}
        </div>
        <button disabled={!affordable || busy} onClick={onRedeem}
          className={affordable ? "neon-btn px-6 py-3" : "neon-outline-btn px-6 py-3"}
          data-testid={`${testIdPrefix}-btn`}>
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Redeeming...</>
            : affordable ? <><Check className="w-4 h-4" /> Redeem</>
            : `Need ${(totalCost - points).toLocaleString()} more`}
        </button>
      </div>
    </div>
  );
}
